import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuthStore } from './store/authStore'
import ProtectedRoute from './components/auth/ProtectedRoute'

// Landing
import LandingPage from './pages/LandingPage'

// Auth
import LoginPage    from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'

// Layout
import AppShell from './components/layout/AppShell'

// Onboarding
import OnboardingPage from './pages/onboarding/OnboardingPage'

// Dashboard
import DashboardPage from './pages/dashboard/DashboardPage'

// Thesis
import ThesisSetupPage   from './pages/thesis/ThesisSetupPage'
import ToCBuilderPage    from './pages/thesis/ToCBuilderPage'
import ChapterHubPage    from './pages/thesis/ChapterHubPage'
import ChapterEditorPage from './pages/thesis/ChapterEditorPage'

// Documents
import SOPPage          from './pages/documents/SOPPage'
import SynopsisPage     from './pages/documents/SynopsisPage'
import ResearchPaperPage from './pages/documents/ResearchPaperPage'

// Research
import ResearchAssistantPage from './pages/research/ResearchAssistantPage'

// References
import ReferencesPage from './pages/references/ReferencesPage'

// Profile
import ProfilePage from './pages/profile/ProfilePage'

// Files
import FilesPage from './pages/files/FilesPage'

// Admin
import AdminPage from './pages/admin/AdminPage'

// Billing
import BillingPage from './pages/billing/BillingPage'

// 404
import NotFoundPage from './pages/NotFoundPage'

function App() {
  const { isAuthenticated, profile, isLoading, loadUser } = useAuthStore()

  useEffect(() => {
    loadUser()
  }, [loadUser])

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: '#f8fafc' }}
      >
        <div className="text-center">
          <div className="font-display font-bold text-2xl mb-2" style={{ color: '#2563eb' }}>
            AcdyON Scholar
          </div>
          <div className="font-sans text-sm" style={{ color: '#64748b' }}>
            Loading…
          </div>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />

      {/* Auth — redirect authenticated users away */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/app/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/app/dashboard" replace /> : <RegisterPage />}
      />

      {/* Onboarding — requires auth, skip if already complete */}
      <Route
        path="/onboarding"
        element={
          !isAuthenticated
            ? <Navigate to="/login" replace />
            : profile?.onboarding_complete
            ? <Navigate to="/app/dashboard" replace />
            : <OnboardingPage />
        }
      />

      {/* Protected app shell */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="profile"   element={<ProfilePage />} />

        {/* Thesis */}
        <Route path="thesis"                             element={<ThesisSetupPage />} />
        <Route path="thesis/:id/toc"                     element={<ToCBuilderPage />} />
        <Route path="thesis/:id/chapters"                element={<ChapterHubPage />} />
        <Route path="thesis/:id/chapters/:chapterId"     element={<ChapterEditorPage />} />

        {/* Documents */}
        <Route path="documents/sop"            element={<SOPPage />} />
        <Route path="documents/synopsis"       element={<SynopsisPage />} />
        <Route path="documents/research-paper" element={<ResearchPaperPage />} />

        {/* Research */}
        <Route path="research" element={<ResearchAssistantPage />} />

        {/* References */}
        <Route path="references" element={<ReferencesPage />} />

        {/* Files */}
        <Route path="files" element={<FilesPage />} />

        {/* Billing */}
        <Route path="billing" element={<BillingPage />} />

        {/* Settings */}
        <Route path="settings" element={<ProfilePage />} />
      </Route>

      {/* Admin — standalone pages, not inside AppShell */}
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin/*" element={<AdminPage />} />

      {/* Legacy shortcuts */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App
